import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl } from "../components/Seo";
import { domainOf, outboundHref, slugify } from "../lib/utils-tv";
import { API, recordLaunch } from "../lib/api";
import { fmtList, tiersText, hasFreeTier, softwareAppLd } from "../lib/compare";
import SponsoredSlots from "../components/SponsoredSlots";

export default function Alternatives() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    let alive = true;
    // Deferred to a microtask so setStatus isn't a synchronous state update in
    // the effect body (satisfies react-hooks/set-state-in-effect).
    Promise.resolve().then(() => {
      if (!alive) return;
      setStatus("loading");
      axios
        .get(`${API}/alternatives/${encodeURIComponent(slug)}`)
        .then((r) => {
          if (!alive) return;
          setData(r.data);
          setStatus("ok");
        })
        .catch((err) => {
          if (!alive) return;
          setStatus(err?.response?.status === 404 ? "notfound" : "error");
        });
    });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <Shell testid="alternatives-loading" path={`/alternatives/${slug}`} noindex title="Loading…">
        <div className="text-[13px] text-[#888]">
          loading alternatives...<span className="tv-blink">_</span>
        </div>
      </Shell>
    );
  }

  if (status === "notfound") {
    return (
      <Shell testid="alternatives-404" path={`/alternatives/${slug}`} noindex title="Not found">
        <h1 className="text-xl font-bold border-b border-black pb-2">
          {">"}_ 404 / no such tool
        </h1>
        <p className="mt-4 text-[13px] text-[#444]">
          That slug isn&apos;t in the index.
        </p>
        <p className="mt-4">
          <Link to="/" className="tv-btn">[← back to index]</Link>
        </p>
      </Shell>
    );
  }

  if (status === "error" || !data?.tool) {
    return (
      <Shell testid="alternatives-error" path={`/alternatives/${slug}`} noindex title="Error">
        <h1 className="text-xl font-bold">{">"}_ error</h1>
        <p className="text-[13px] text-[#444] mt-2">could not load. try again.</p>
      </Shell>
    );
  }

  const tool = data.tool;
  const alts = data.alternatives || [];
  const path = `/alternatives/${tool.slug}`;
  const seoTitle = `Best ${tool.title} alternatives`;
  const freeAlts = alts.filter((a) => hasFreeTier(a));
  const ossAlts = alts.filter((a) => a.is_open_source);
  const otherAsset = alts.filter((a) => a.asset_class && tool.asset_class && a.asset_class !== tool.asset_class);
  const seoDesc =
    `${alts.length} alternatives to ${tool.title} compared on pricing, best-for and who uses them` +
    (freeAlts.length ? ` — ${freeAlts.length} free` : "") +
    (ossAlts.length ? `, ${ossAlts.length} open source` : "") +
    (tool.pricing ? `. ${tool.title} is ${tool.pricing}.` : ".");

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Alternatives to ${tool.title}`,
    numberOfItems: alts.length,
    itemListElement: alts.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: softwareAppLd(a, siteUrl),
    })),
  };

  // FAQ schema — answers built strictly from real DB fields (no invention).
  const names = (list) => list.map((a) => a.title).join(", ");
  const faqEntries = [
    {
      q: `What is the best alternative to ${tool.title}?`,
      a: alts[0] ? `${alts[0].title} — ${alts[0].best_for || alts[0].description}` : null,
    },
    {
      q: `Is there a free alternative to ${tool.title}?`,
      a: freeAlts.length
        ? `Yes — ${names(freeAlts)} ${freeAlts.length === 1 ? "has" : "have"} a free tier.`
        : null,
    },
    {
      q: `Are there open-source alternatives to ${tool.title}?`,
      a: ossAlts.length ? `Yes — ${names(ossAlts)}.` : null,
    },
    {
      q: `How much does ${tool.title} cost?`,
      a: tiersText(tool) ? `${tool.title} plans: ${tiersText(tool)}.` : tool.pricing ? `${tool.title} pricing tier: ${tool.pricing}.` : null,
    },
    { q: `What is ${tool.title} best for?`, a: tool.best_for || tool.description },
  ].filter((f) => f.a);
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntries.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <Shell
      testid="alternatives-page"
      path={path}
      title={seoTitle}
      description={seoDesc}
      image={siteUrl(`/api/og/tool/${tool.slug}.png?v=4`)}
      ogType="article"
      jsonLd={[itemListLd, faqLd]}
    >
      {/* Breadcrumb */}
      <div className="text-[12px] text-[#888] mb-3" data-testid="alternatives-breadcrumbs">
        <Link to="/" className="tv-link">index</Link>{" / "}
        <Link to={`/tool/${tool.slug}`} className="tv-link">{tool.title}</Link>{" / "}
        <span className="text-black">alternatives</span>
      </div>

      <h1
        className="text-xl font-bold border-b border-black pb-2"
        data-testid="alternatives-title"
      >
        {">"}_ alternatives to {tool.title}
      </h1>

      <p className="mt-4 text-[14px] text-[#222] leading-relaxed" data-testid="alternatives-intro">
        {alts.length > 0 ? (
          <>
            {alts.length} {tool.category ? tool.category.toLowerCase() : "investing"}{" "}
            {alts.length === 1 ? "tool" : "tools"} that do a similar job to{" "}
            {tool.title}, with the facts side-by-side.
          </>
        ) : (
          <>No close alternatives to {tool.title} in the index yet.</>
        )}
      </p>

      {/* Prominent profile anchor — passes internal link equity straight back
          to the parent tool profile. Descriptive anchor text helps Google tie
          this page to the core profile page. */}
      <p className="mt-3 text-[14px] text-[#222] leading-relaxed" data-testid="alternatives-profile-link">
        See full data, pricing and features on the{" "}
        <Link to={`/tool/${tool.slug}`} className="tv-link font-bold" data-testid="alternatives-profile-anchor">
          {tool.title} profile page
        </Link>
        .
      </p>

      {/* The reference tool */}
      <div className="mt-6 border border-black p-3" data-testid="alternatives-reference">
        <div className="text-[11px] uppercase tracking-wide text-[#888] mb-1">
          the tool you&apos;re replacing
        </div>
        <AltRow tool={tool} reference />
        {tool.overview && (
          <p className="mt-3 text-[13px] text-[#222] leading-relaxed" data-testid="alternatives-overview">
            {tool.overview}
          </p>
        )}
      </div>

      {/* At-a-glance comparison table — the reference tool vs every alternative
          across best-for / pricing / who-uses-it. High data density + scannable,
          built only from real DB fields. */}
      {alts.length > 0 && (
        <div className="mt-6 border border-black overflow-x-auto" data-testid="alternatives-table">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-black text-white text-left">
                <th className="px-2 py-1.5 font-bold">tool</th>
                <th className="px-2 py-1.5 font-bold border-l border-[#444]">best for</th>
                <th className="px-2 py-1.5 font-bold border-l border-[#444]">pricing</th>
                <th className="px-2 py-1.5 font-bold border-l border-[#444]">plans</th>
                <th className="px-2 py-1.5 font-bold border-l border-[#444]">who uses it</th>
              </tr>
            </thead>
            <tbody>
              {[tool, ...alts].map((t, i) => (
                <tr
                  key={t.id}
                  className={`${i % 2 ? "bg-[#FBF8EE]" : "bg-white"} border-t border-[#eee]`}
                  data-testid={`alt-cmp-row-${t.slug}`}
                >
                  <td className="px-2 py-1.5 align-top">
                    <Link to={`/tool/${t.slug}`} className="tv-link font-bold">{t.title}</Link>
                    {t.slug === tool.slug && <span className="text-[10px] text-[#888]"> (this tool)</span>}
                    {t.slug !== tool.slug && (
                      <div className="text-[10px] mt-0.5">
                        <Link
                          to={`/vs/${[tool.slug, t.slug].sort().join("-vs-")}`}
                          className="tv-link"
                          data-testid={`alt-cmp-vs-${t.slug}`}
                        >
                          ⇄ vs {tool.title}
                        </Link>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top border-l border-[#eee]">
                    {t.best_for || t.key_feature || "—"}
                  </td>
                  <td className="px-2 py-1.5 align-top border-l border-[#eee] font-bold tabular-nums">
                    {t.pricing || "$?"}
                    {hasFreeTier(t) && <div className="text-[10px] font-normal text-[#166534]">free tier</div>}
                  </td>
                  <td className="px-2 py-1.5 align-top border-l border-[#eee] text-[12px]" data-testid={`alt-cmp-plans-${t.slug}`}>
                    {tiersText(t) || "—"}
                  </td>
                  <td className="px-2 py-1.5 align-top border-l border-[#eee]">
                    {fmtList(t.user_base) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SponsoredSlots placement={`alternatives:${tool.slug}`} />

      {/* Sliced lists — deterministic from stored fields, each an H2 crawlers
          can map to "free / open-source / other-asset alternatives" queries. */}
      {(freeAlts.length > 0 || ossAlts.length > 0 || otherAsset.length > 0) && (
        <div className="mt-8 space-y-5" data-testid="alternatives-slices">
          {freeAlts.length > 0 && (
            <Slice title={`free alternatives to ${tool.title}`} items={freeAlts} testid="alternatives-free" />
          )}
          {ossAlts.length > 0 && (
            <Slice title={`open-source alternatives to ${tool.title}`} items={ossAlts} testid="alternatives-oss" />
          )}
          {otherAsset.length > 0 && (
            <Slice
              title={`${tool.title} alternatives for other asset classes`}
              items={otherAsset}
              testid="alternatives-other-asset"
              extra={(a) => a.asset_class}
            />
          )}
        </div>
      )}

      {/* Alternatives list */}
      {alts.length > 0 ? (
        <ul className="list-none p-0 m-0 mt-6 space-y-3" data-testid="alternatives-list">
          {alts.map((a) => (
            <li key={a.id} className="border-b border-[#eee] pb-3">
              <AltRow tool={a} compareWith={tool} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-6 text-[13px] text-[#666]" data-testid="alternatives-empty">
          No curated alternatives yet. Browse the{" "}
          <Link to={`/f/${slugify(tool.category || "")}`} className="tv-link">
            full {tool.category || "tools"} list
          </Link>
          .
        </p>
      )}

      <div className="mt-8 flex flex-wrap gap-2">
        <Link to={`/tool/${tool.slug}`} className="tv-btn" data-testid="alternatives-back-tool">
          [← {tool.title} details]
        </Link>
        <Link to="/" className="tv-btn" data-testid="alternatives-back-index">
          [← back to index]
        </Link>
      </div>
    </Shell>
  );
}

function Slice({ title, items, testid, extra }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-[15px] font-bold border-b border-[#ddd] pb-1">{title}</h2>
      <ul className="mt-2 pl-5 list-disc text-[13px] text-[#222] leading-relaxed space-y-1">
        {items.map((a) => (
          <li key={a.slug}>
            <Link to={`/tool/${a.slug}`} className="tv-link font-bold">{a.title}</Link>
            {extra?.(a) ? <span className="text-[#666]"> ({extra(a)})</span> : null}
            {" — "}
            {a.best_for || a.key_feature || a.description}
            {tiersText(a) ? <span className="text-[#666]"> · {tiersText(a)}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function AltRow({ tool: t, reference = false, compareWith = null }) {
  const price = t.pricing || "$?";
  return (
    <div data-testid={`alt-row-${t.slug}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <Link to={`/tool/${t.slug}`} className="tv-link font-bold text-[15px]" data-testid={`alt-name-${t.slug}`}>
          {t.title}
        </Link>
        <span className="text-[12px] text-[#666]">— {t.description}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-x-2 gap-y-1 flex-wrap text-[12px]">
        {t.asset_class && (
          <Link to={`/c/${slugify(t.asset_class)}`} className="border border-black px-1.5 py-0.5 hover:bg-black hover:text-white no-underline text-black">
            {t.asset_class}
          </Link>
        )}
        {t.category && (
          <Link to={`/f/${slugify(t.category)}`} className="border border-black px-1.5 py-0.5 hover:bg-black hover:text-white no-underline text-black">
            {t.category}
          </Link>
        )}
        <span className="font-bold tabular-nums">{price}</span>
        {t.key_feature && <span className="text-[#888]">· {t.key_feature}</span>}
      </div>
      {!reference && (
        <div className="mt-2 flex flex-wrap gap-2">
          <a
            href={outboundHref(t, "alternatives")}
            target="_blank"
            rel="sponsored noopener noreferrer"
            onClick={() => recordLaunch(t.id)}
            className="tv-btn tv-btn-primary"
            data-testid={`alt-visit-${t.slug}`}
          >
            [↗] visit {domainOf(t.url)}
          </a>
          {compareWith && (
            <Link
              to={`/vs/${[compareWith.slug, t.slug].sort().join("-vs-")}`}
              className="tv-btn"
              data-testid={`alt-compare-${t.slug}`}
            >
              [⇄] {compareWith.title} vs {t.title}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function Shell({ testid, children, ...seo }) {
  return (
    <div data-testid={testid}>
      <Seo {...seo} />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">{children}</main>
      <Footer />
    </div>
  );
}
